import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBagShopping, faPrint } from '@fortawesome/pro-light-svg-icons';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Button } from '@shared/components/ui/button';
import { Shop } from '@shared/components/ui/shop';
import { Badge } from '@shared/components/ui/badge';
import { MassivePackingGroup, MassivePackingOrder } from '../../models';
import { PreparationStatus } from '@modules/OrderPreparation/shared/config';

export interface GroupOrdersModalRef {
  open: (group: MassivePackingGroup) => void;
  close: () => void;
}

interface GroupOrdersModalProps {
  onPrintOrder: (order: MassivePackingOrder) => Promise<void>;
}

export const GroupOrdersModal = forwardRef<GroupOrdersModalRef, GroupOrdersModalProps>(({ onPrintOrder }, ref) => {
  const { t } = useTranslation('massivePackingModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const [group, setGroup] = useState<MassivePackingGroup | null>(null);
  const [printingOrderId, setPrintingOrderId] = useState<number | null>(null);

  useImperativeHandle(ref, () => ({
    open: (selectedGroup: MassivePackingGroup) => {
      setGroup(selectedGroup);
      modalRef.current?.openModal();
    },
    close: () => {
      modalRef.current?.closeModal();
      setGroup(null);
    },
  }));

  const handlePrint = async (order: MassivePackingOrder) => {
    if (printingOrderId) return;
    setPrintingOrderId(order.id);
    try {
      await onPrintOrder(order);
    } finally {
      setPrintingOrderId(null);
    }
  };

  const buildImageUrl = (imageHash: string | undefined | null) => {
    if (!imageHash) return '';
    if (imageHash.startsWith('http')) return imageHash;
    return `https://warehouse-images-stg-dev.nyc3.digitaloceanspaces.com/${imageHash}`;
  };

  const renderOrderRow = (order: MassivePackingOrder) => {
    const isPrepared = !!order.shipment?.processed_at;
    const statusClass = isPrepared
      ? 'bg-bg-success text-success border-success'
      : 'bg-card-border text-subtext border-card-border';

    const statusLabel = isPrepared ? t('detail.order_prepared') : t('detail.order_to_prepare');

    return (
      <div
        key={order.id}
        className="flex flex-col gap-3 p-4 border rounded-lg border-card-border bg-card-bg md:flex-row md:items-center md:justify-between md:gap-4"
      >
        {/* Fila(s) móvil: bloque tienda + badge */}
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-4">
          {order.shop ? (
            <Shop src={buildImageUrl(order.shop.image_hash)} name={order.shop.name} code={order.ref} className="py-0" />
          ) : (
            <div className="flex flex-col gap-1">
              <span className="font-medium text-title">{order.ref}</span>
            </div>
          )}
          <Badge className={`w-fit border ${statusClass}`}>{statusLabel}</Badge>
        </div>

        {/* Fila(s) móvil: transportista a la izquierda, botón imprimir a la derecha */}
        <div className="flex flex-row items-center justify-between gap-2 md:gap-4">
          {order.shipment?.courier ? (
            <Shop
              src={buildImageUrl(order.shipment.courier.image_hash)}
              name={order.shipment.courier.name}
              code={order.shipment.tracking_number}
              className="py-0"
            />
          ) : (
            <span />
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePrint(order)}
            disabled={!isPrepared || printingOrderId === order.id}
            title={t('actions.print_labels')}
            className="p-2 h-8 w-8 flex-shrink-0 text-icons hover:text-title disabled:opacity-50 border-card-border"
          >
            <FontAwesomeIcon
              icon={faPrint}
              className={`w-3 h-3 ${printingOrderId === order.id ? 'animate-pulse' : ''}`}
            />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <ModalCenter
      ref={modalRef}
      title={
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faBagShopping} className="w-5 h-5" />
          <span>{t('modals.group_orders.title')}</span>
        </div>
      }
      type="default"
      width="xl"
    >
      <div className="space-y-3 max-h-[60vh] overflow-y-auto p-1">
        {group?.orders.map(renderOrderRow)}
        {(!group || group.orders.length === 0) && (
          <div className="text-center py-8 text-subtext">{t('detail.empty')}</div>
        )}
      </div>
    </ModalCenter>
  );
});

GroupOrdersModal.displayName = 'GroupOrdersModal';
